import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageSquareText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RequestTyreCta({ brandModel = "", size = "", productId = "", testId = "request-tyre-cta" }) {
  const qs = new URLSearchParams({ ...(brandModel && { brand_model: brandModel }), ...(size && { tyre_size: size }), ...(productId && { product_id: productId }) });
  return (
    <div className="request-cta" data-testid={testId}>
      <MessageSquareText size={22} />
      <div>
        <b>Can’t find your tyre?</b>
        <span>Tell us the size you need and we’ll source it for you.</span>
      </div>
      <Link to={`/request-tyre${qs.toString() ? `?${qs}` : ""}`} className="btn btn-outline" data-testid={`${testId}-link`}>
        Request a tyre <ArrowRight size={15} />
      </Link>
    </div>
  );
}

const FIELDS = [
  ["name", "Your name *", { required: true, minLength: 2, maxLength: 100 }],
  ["email", "Email *", { required: true, type: "email", maxLength: 120 }],
  ["phone", "Phone (optional)", { type: "tel", maxLength: 30 }],
  ["brand_model", "Tyre brand & model (if known)", { maxLength: 120 }],
  ["tyre_size", "Tyre size * (e.g. 225/45 R17)", { required: true, minLength: 3, maxLength: 40 }],
  ["vehicle", "Vehicle make, model & year", { maxLength: 120 }],
  ["quantity", "Quantity *", { required: true, type: "number", min: 1, max: 100 }],
];

function Confirmation({ result, email }) {
  return (
    <div className="request-done" data-testid="request-tyre-success">
      <CheckCircle2 size={34} />
      <h2>{result.duplicate ? "We already have this request." : "Request received."}</h2>
      <p data-testid="request-tyre-reference">Reference {result.enquiry_id}</p>
      <p>
        {result.duplicate
          ? "You sent a request for this size recently. Our team is on it and will reply to "
          : result.notified
            ? "Our team has been notified and will reply to "
            : "Your request is saved and our team will reply to "}
        <b>{email}</b>.
      </p>
      <Link to="/catalog/tires" className="btn btn-red" data-testid="request-tyre-back">
        Back to tyres
      </Link>
    </div>
  );
}

export default function RequestTyrePage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", brand_model: params.get("brand_model") || "", tyre_size: params.get("tyre_size") || "",
    vehicle: "", quantity: 4, notes: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/enquiries`, { ...form, quantity: Number(form.quantity), product_id: params.get("product_id") || null });
      setResult(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? `Please check ${d[0]?.loc?.slice(-1)[0]?.replace("_", " ")}: ${d[0]?.msg}` : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="request-page">
      <div className="request-intro">
        <p className="eyebrow red">SOURCING / ON REQUEST</p>
        <h1>
          Request
          <br />
          <i>a tyre.</i>
        </h1>
        <p>Not listed? Send us the size and we’ll check availability and pricing for you.</p>
      </div>
      {result ? (
        <Confirmation result={result} email={form.email} />
      ) : (
        <form className="request-form" onSubmit={submit} data-testid="request-tyre-form">
          {FIELDS.map(([key, label, attrs]) => (
            <label key={key}>
              {label}
              <input {...attrs} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`request-${key.replace("_", "-")}`} />
            </label>
          ))}
          <label className="wide">
            Additional requirements
            <textarea maxLength={2000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="request-notes" />
          </label>
          <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} name="website" />
          {error && (
            <p className="form-error wide" data-testid="request-tyre-error">
              {error}
            </p>
          )}
          <button className="btn btn-red" disabled={busy} data-testid="request-tyre-submit">
            {busy ? "Sending…" : "Send request"} <ArrowRight size={16} />
          </button>
        </form>
      )}
    </main>
  );
}
